import React, { createContext, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import toastify from '../../lib/toastify';
import { getIdFromURL } from '../../utils/getIdFromURL';
import { scrollWindowTo } from '../../utils/scroll';
import { useQueries } from '@tanstack/react-query';
import { getCurrentAd, getPerformanceMetrics, getQualityTips } from '../../services/Ad.service';
import { CURRENT_AD_QUERY, PERFORMANCE_AD_QUERY, QUALITY_AD_QUERY } from '../../queries';
import { PerformanceContextInterface, PerformanceContextProviderProps } from './types/performance';

export const PerformanceContext = createContext<PerformanceContextInterface | undefined>(undefined);

export function PerformanceContextProvider({
  children
}: Readonly<PerformanceContextProviderProps>): JSX.Element {
  const location = useLocation();
  const history = useHistory();

  const idFromURL = Number(getIdFromURL(location.pathname));

  const [currentAd, performanceMetrics, qualityTips] = useQueries({
    queries: [
      {
        queryFn: () => getCurrentAd(idFromURL),
        enabled: !!idFromURL,
        queryKey: CURRENT_AD_QUERY(idFromURL)
      },
      {
        queryFn: () => getPerformanceMetrics(idFromURL),
        enabled: !!idFromURL,
        queryKey: PERFORMANCE_AD_QUERY(idFromURL)
      },
      {
        queryFn: () => getQualityTips(idFromURL),
        enabled: !!idFromURL,
        queryKey: QUALITY_AD_QUERY(idFromURL)
      }
    ]
  });

  useEffect(() => {
    scrollWindowTo(0);
  }, []);

  useEffect(() => {
    if (currentAd.isLoading) {
      scrollWindowTo(0);
    }

    if (currentAd.isError) {
      toastify.error('falha na requisição', 'não foi possível buscar o anúncio neste momento');
    }
  }, [history, currentAd.isLoading, currentAd.isError]);

  useEffect(() => {
    if (performanceMetrics.isError) {
      toastify.error(
        'falha na requisição',
        'não foi possível buscar estatísticas do seu anúncio neste momento'
      );
    }
  }, [performanceMetrics.isError]);

  return (
    <PerformanceContext.Provider
      value={{
        currentAd,
        performanceMetrics,
        qualityTips
      }}
    >
      {children}
    </PerformanceContext.Provider>
  );
}

export function usePerformanceContext(): PerformanceContextInterface {
  const context = useContext(PerformanceContext);

  if (!context) {
    throw new Error('Ad is not available');
  }

  return context;
}
